import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, EventEmitter, Output, Input } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { OrdersService } from '@evermile/customer-tracking';
import { Proposal } from '@evermile/customer-tracking/model/proposal';
import { DateProposals } from '@evermile/customer-tracking/model/dateProposals';
import { TimeslotProposal } from '@evermile/customer-tracking/model/timeslotProposal';
import { DisplayDateFormatterPipe } from '../pipes/display-date-formatter.pipe';
import { UKTimePipe } from '../pipes/uk-date-formatter.pipe';

export interface RebookDeliveryOption {
  proposalId: string;
  date: string;
  time: string;
}

export const buttonTypeToButtonText = {
  ['newTimeRequest']: 'Suggest new time',
  ['notNow']: 'Not now',
  ['date']: 'Date',
  ['confirm']: 'Confirm time slot',
  ['changeSelection']: 'Change selection',
  ['contactUs']: 'Prefer another time? Contact us',
};

export type ButtonType = 'contactUs' | 'newTimeRequest' | 'notNow' | 'confirm' | 'date' | 'changeSelection';

export type CTAOption = {
  text: string;
  action: () => void;
  type: 'primary' | 'secondary';
  value: ButtonType;
};

export interface ChatMessage {
  type: 'bot' | 'user';
  content: string;
  isCTA?: boolean;
  ctaOptions?: CTAOption[];
  selectedOption?: string;
  isSelected?: boolean;
}

@Component({
  selector: 'common-failed-delivery-rebook-flow',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './failed-delivery-rebook-flow.component.html',
  styleUrls: ['./failed-delivery-rebook-flow.component.scss'],
})
export class FailedDeliveryRebookFlowComponent implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() timeSlotConfirmed = new EventEmitter<RebookDeliveryOption>();
  deliveryOptions: RebookDeliveryOption[] = [];
  isLoading = false;
  showLoadingIndicator = false;

  private shouldScroll = false;
  showMoreOptions = false;
  messages: ChatMessage[] = [
    {
      type: 'bot',
      content: 'We apologize, but your delivery could not be completed. Would you like to suggest a new delivery time?',
      isCTA: true,
      ctaOptions: [
        {
          value: 'newTimeRequest',
          text: buttonTypeToButtonText['newTimeRequest'],
          action: () => this.startTimeSelection(),
          type: 'primary',
        },
        {
          value: 'notNow',
          text: buttonTypeToButtonText['notNow'],
          action: () => this.handleNotNow(),
          type: 'secondary',
        },
      ],
    },
  ];

  newSelectedSlot?: RebookDeliveryOption | null;

  constructor(
    private apiEditOrderService: OrdersService,
    private timePipeUK: UKTimePipe,
    private displayDateFormatterPipe: DisplayDateFormatterPipe,
  ) {}

  ngOnInit(): void {
    this.getProposalsToRebook();
  }

  getProposalsToRebook() {
    this.isLoading = true;
    this.apiEditOrderService.orderEditQuotePost({}).subscribe(
      (res) => {
        this.deliveryOptions = [];
        res.dateProposals.forEach((dateProposal: DateProposals) => {
          dateProposal.proposals.forEach((proposal: TimeslotProposal) => {
            if (
              proposal.proposal?.estimatedDropoff?.start &&
              proposal.proposal.estimatedDropoff.end &&
              this.deliveryOptions.length < 8
            ) {
              this.deliveryOptions.push({
                proposalId: proposal.proposal.id,
                date: this.displayDateFormatterPipe.transformMerchantTime(dateProposal.date, 'E, MMM d') || '',
                time:
                  this.timePipeUK.transformWithFormat(proposal.proposal.estimatedDropoff.start, 'h:mmaaa') +
                    ' - ' +
                    this.timePipeUK.transformWithFormat(proposal.proposal.estimatedDropoff.end, 'h:mmaaa') || '',
              });
            }
          });
        });
        this.isLoading = false;
        this.showLoadingIndicator = false;

        // Update the time options if the user has already initiated the time selection flow
        if (this.messages.some((msg) => msg.type === 'bot' && msg.content.includes('Thanks!'))) {
          this.updateTimeOptions();
        }
      },
      (error) => {
        console.error('Error fetching rebook proposals:', error);
        this.isLoading = false;
        this.showLoadingIndicator = false;

        // Show error message if the user has initiated the time selection flow
        if (this.messages.some((msg) => msg.type === 'bot' && msg.content.includes('Thanks!'))) {
          const thanksMessageIndex = this.messages.findIndex(
            (msg) => msg.type === 'bot' && msg.content.includes('Thanks!'),
          );

          if (thanksMessageIndex !== -1) {
            this.messages[thanksMessageIndex].content =
              "Sorry, we couldn't load delivery slots at this time. Please try again later.";
          }
        }
      },
    );
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  private scrollToBottom(): void {
    try {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  private setShouldScroll(): void {
    this.shouldScroll = true;
  }

  startTimeSelection(): void {
    // Reset the showMoreOptions flag
    this.showMoreOptions = false;

    // Remove CTA options from all messages
    this.messages.forEach((message) => {
      message.isCTA = false;
      message.ctaOptions = undefined;
    });

    // Add user's selection as a message
    this.messages.push({
      type: 'user',
      content: buttonTypeToButtonText['newTimeRequest'],
      isSelected: true,
    });
    this.setShouldScroll();

    // Add bot's response with time options
    setTimeout(() => {
      this.messages.push({
        type: 'bot',
        content:
          'Thanks! Here are a few suggested delivery slots you can choose from (all times are shown in UK time):',
        isCTA: true,
        ctaOptions: this.deliveryOptions.slice(0, 3).map((option) => ({
          text: `${option.date} - ${option.time}`,
          value: 'date',
          action: () => this.selectTimeOption(option),
          type: 'primary',
        })),
      });
      this.setShouldScroll();
    }, 500);

    // Show loading indicator
    this.showLoadingIndicator = true;

    // Set loading state and get proposals if needed
    this.isLoading = true;
    if (this.deliveryOptions.length === 0) {
      // We need to fetch the delivery options from the API
      this.getProposalsToRebook();
    } else {
      // If we already have the delivery options, show a brief typing animation
      // then update the message with the options
      setTimeout(() => {
        this.isLoading = false;
        this.showLoadingIndicator = false;
        this.updateTimeOptions();
      }, 1200);
    }
  }

  private updateTimeOptions(): void {
    // Find the "Thanks!" message
    const thanksMessageIndex = this.messages.findIndex((msg) => msg.type === 'bot' && msg.content.includes('Thanks!'));

    if (thanksMessageIndex !== -1) {
      // Update the message with time options
      this.messages[thanksMessageIndex].isCTA = true;
      this.messages[thanksMessageIndex].ctaOptions = this.deliveryOptions.slice(0, 3).map((option) => ({
        text: `${option.date} - ${option.time}`,
        value: 'date',
        action: () => this.selectTimeOption(option),
        type: 'primary',
      }));
      this.setShouldScroll();
    }
  }

  handleNotNow(): void {
    // Remove CTA options from all messages
    this.messages.forEach((message) => {
      message.isCTA = false;
      message.ctaOptions = undefined;
    });

    // Add user's selection as a message
    this.messages.push({
      type: 'user',
      content: buttonTypeToButtonText['notNow'],
      isSelected: true,
    });
    this.setShouldScroll();

    // Add bot's response
    setTimeout(() => {
      this.messages.push({
        type: 'bot',
        content: 'No problem! You can reschedule your delivery at any time.',
        isCTA: true,
        ctaOptions: [
          {
            value: 'newTimeRequest',
            text: buttonTypeToButtonText['newTimeRequest'],
            action: () => this.startTimeSelection(),
            type: 'primary',
          },
        ],
      });
      this.setShouldScroll();
    }, 500);
  }

  selectTimeOption(option: RebookDeliveryOption): void {
    // Remove CTA options from all messages
    this.messages.forEach((message) => {
      message.isCTA = false;
      message.ctaOptions = undefined;
    });

    // Remove the contact message if it exists
    this.messages = this.messages.filter(
      (message) => !(message.type === 'bot' && message.content === "Can't find a time that works for you? Contact us"),
    );

    // Add user's selection as a message
    this.messages.push({
      type: 'user',
      content: `${option.date} - ${option.time}`,
      isSelected: true,
    });
    this.setShouldScroll();

    // Add verification message with CTAs
    setTimeout(() => {
      this.messages.push({
        type: 'bot',
        content: `You've selected ${option.date} between ${option.time} (UK time). Please note that this is a request that will be sent to the merchant for confirmation. Would you like to proceed with this time slot?`,
        isCTA: true,
        ctaOptions: [
          {
            value: 'confirm',
            text: buttonTypeToButtonText['confirm'],
            action: () => this.confirmTimeSlot(option),
            type: 'primary',
          },
          {
            value: 'changeSelection',
            text: buttonTypeToButtonText['changeSelection'],
            action: () => this.startTimeSelection(),
            type: 'secondary',
          },
        ],
      });
      this.setShouldScroll();
    }, 500);
  }

  confirmTimeSlot(option: RebookDeliveryOption): void {
    // Remove CTA options from all messages
    this.messages.forEach((message) => {
      message.isCTA = false;
      message.ctaOptions = undefined;
    });

    // Add user's confirmation as a message
    this.messages.push({
      type: 'user',
      content: buttonTypeToButtonText['confirm'],
      isSelected: true,
    });
    this.setShouldScroll();
    this.newSelectedSlot = option;

    this.timeSlotConfirmed.emit(option);
  }

  confirmTimSlotUpdated() {
    if (!this.newSelectedSlot) return;
    this.messages.push({
      type: 'bot',
      content: `Great! Your delivery request for ${this.newSelectedSlot.date} between ${this.newSelectedSlot.time} (UK time) has been sent to the merchant. You'll receive a confirmation once they approve the time slot.`,
    });
    this.setShouldScroll();
  }

  showErrorMessage() {
    this.messages.push({
      type: 'bot',
      content: `Sorry, something went wrong. Please contact us.`,
      isCTA: true,
      ctaOptions: [
        {
          value: 'contactUs',
          text: buttonTypeToButtonText['contactUs'],
          action: () => this.handleContactUs(),
          type: 'secondary',
        },
      ],
    });
    this.setShouldScroll();
  }

  toggleMoreOptions(): void {
    this.showMoreOptions = true;

    // Find the message with time slots
    const timeSlotMessage = this.messages.find(
      (msg) => msg.type === 'bot' && msg.content.includes('suggested delivery slots'),
    );

    if (timeSlotMessage) {
      // Create a new message with all time slots
      const newMessage: ChatMessage = {
        type: 'bot',
        content: 'Here are all available delivery slots:',
        isCTA: true,
        ctaOptions: this.deliveryOptions.map((option) => ({
          text: `${option.date} - ${option.time}`,
          value: 'date',
          action: () => this.selectTimeOption(option),
          type: 'primary',
        })),
      };

      // Replace the old message with the new one
      const index = this.messages.indexOf(timeSlotMessage);
      this.messages[index] = newMessage;
      this.setShouldScroll();

      // Add the contact CTA after a short delay
      setTimeout(() => {
        this.messages.push({
          type: 'bot',
          content: "Can't find a time that works for you? Contact us",
          isCTA: true,
          ctaOptions: [
            {
              value: 'contactUs',
              text: buttonTypeToButtonText['contactUs'],
              action: () => this.handleContactUs(),
              type: 'secondary',
            },
          ],
        });
        this.setShouldScroll();
      }, 500);
    }
  }

  handleContactUs(): void {
    // Remove CTA options from all messages
    this.messages.forEach((message) => {
      message.isCTA = false;
      message.ctaOptions = undefined;
    });
    this.setShouldScroll();
    this.handleClose();

    //todo open chat
  }

  handleClose(): void {
    // Emit close event
    this.closeEvent.emit();
    this.newSelectedSlot = null;
  }
}
